<template>
    <module-card :nextDisabled="nextDisabled">
        <FlipCard :flipped="flipped">
            <template v-slot:front>
                <p>
                    Here are a few examples that P, Art, Nico, and Tommy have had to deal with. Match each scenario with the correct form of stigma management strategy.
                </p>

                <v-container grid-list-xs>
                    <v-row wrap justify-center no-gutters>
                        <v-col v-for="peep in peeps" :key="peep.id" cols="6">
                            <a @click.prevent="select(peep)">
                                <v-img :src="peep.image" alt="peep.name" :class="{ completed: peep.complete }">
                                    <v-img v-if="peep.complete" :src="require('@/assets/underwearPersonality/profiles/checkmark.png')" class="check"></v-img>
                                </v-img>
                            </a>
                        </v-col>
                    </v-row>
                </v-container>
            </template>
            <template v-slot:back>
                <div v-if="selectedPeep">
                    <v-carousel v-model="comicIndex" height="320" :continuous="false" hide-delimiters @change="change">
                        <v-carousel-item v-for="(comic, index) in peeps[selectedPeep].comics" :key="index" :src="comic" contain></v-carousel-item>
                    </v-carousel>

                    <div v-if="viewedEntireComic">
                        <p>Which strategy is {{ peeps[selectedPeep].name }} using?</p>
                        <div v-for="strategy in strategies" :key="strategy.id" :class="[isStrategySelected(strategy.id) ? 'primary white--text' : 'grey lighten-3']" class="pa-3 mb-1" @click.stop="selectStrategy(strategy)" v-ripple>
                            <span :class="{ 'incorrect-selected': isIncorrectAlreadySelected(strategy) }">{{ strategy.strategy }}</span>
                        </div>

                        <v-row>
                            <v-spacer></v-spacer>
                            <v-btn large color="primary" @click="flippedNextClick()" :disabled="flippedNextDisabled()">Next</v-btn>
                            <v-spacer></v-spacer>
                        </v-row>
                    </div>
                </div>
            </template>
        </FlipCard>
        <template v-if="flipped" v-slot:actions>
            <v-spacer></v-spacer>
        </template>
        <ModalFeedback ref="modalFeedback"></ModalFeedback>
    </module-card>
</template>

<script>
    import { keyBy, every, shuffle, includes } from 'lodash';
    export default {
        data() {
            return {
                flipped: false,
                selectedPeep: null,
                selectedStrategy: null,
                viewedEntireComic: false,
                comicIndex: 0,
                incorrectSelected: [],
                strategies: [
                    {
                        id: 1,
                        strategy: 'Passing'
                    },
                    {
                        id: 2,
                        strategy: 'Throwing in the Towel'
                    },
                    {
                        id: 3,
                        strategy: 'Playing the Part'
                    },
                    {
                        id: 4,
                        strategy: 'Standing United'
                    }
                ],
                correctMessage: `That's right!`,
                incorrectMessages: ['Good try, give it another shot', 'Not Quite! Try again', 'Close!']
            };
        },
        methods: {
            selectStrategy(strategy) {
                if (strategy.id != this.peeps[this.selectedPeep].strategy) {
                    this.$refs.modalFeedback.show(false, shuffle(this.incorrectMessages)[0], () => {
                        if (!includes(this.incorrectSelected, strategy.id)) {
                            this.incorrectSelected.push(strategy.id);
                        }
                    });
                } else {
                    this.$refs.modalFeedback.show(true, this.correctMessage, () => {
                        this.selectedStrategy = strategy;
                        this.done(this.peeps[this.selectedPeep]);
                    });
                }
            },
            isStrategySelected(id) {
                if (this.selectedStrategy && parseInt(id) === parseInt(this.selectedStrategy.id)) return true;
                else return false;
            },
            select(peep) {
                this.incorrectSelected = [];
                this.selectedPeep = peep.id;
                this.flipped = true;
                this.selectedStrategy = null;
                this.viewedEntireComic = false;
                this.comicIndex = 0;
            },
            done(peep) {
                peep.complete = true;
                this.flipped = false;
            },
            change(i) {
                if (i + 1 === this.peeps[this.selectedPeep].comics.length) {
                    this.viewedEntireComic = true;
                }
            },
            nextDisabled() {
                // disable next until all peeps have been visited
                return !every(this.peeps, (p) => {
                    return p.complete;
                });
            },
            flippedNextClick() {
                if (this.selectedStrategy.id != this.peeps[this.selectedPeep].strategy) {
                    this.$refs.modalFeedback.show(false, shuffle(this.incorrectMessages)[0], null);
                } else {
                    this.done(this.peeps[this.selectedPeep]);
                }
            },
            flippedNextDisabled() {
                return this.selectedStrategy === null;
            },
            isIncorrectAlreadySelected(strategy) {
                return this.incorrectSelected.includes(strategy.id);
            }
        },
        computed: {
            peeps() {
                let peeps = keyBy(
                    [
                        {
                            id: 1,
                            name: 'Philip',
                            image: require('@/assets/underwearPersonality/profiles/characters_Philip.png'),
                            comics: [require('@/assets/4WaystoManageStigma/Comic-P/Comic06-1.png'), require('@/assets/4WaystoManageStigma/Comic-P/Comic06-2.png')],
                            strategy: 2,
                            complete: false
                        },
                        {
                            id: 2,
                            name: 'Art',
                            image: require('@/assets/underwearPersonality/profiles/characters_Art.png'),
                            comics: [
                                require('@/assets/4WaystoManageStigma/Comic-Art/Comic05-1.png'),
                                require('@/assets/4WaystoManageStigma/Comic-Art/Comic05-2.png'),
                                require('@/assets/4WaystoManageStigma/Comic-Art/Comic05-3.png'),
                                require('@/assets/4WaystoManageStigma/Comic-Art/Comic05-4.png')
                            ],
                            strategy: 1,
                            complete: false
                        },
                        {
                            id: 3,
                            name: 'Nico',
                            image: require('@/assets/underwearPersonality/profiles/characters_Nico.png'),
                            comics: [require('@/assets/4WaystoManageStigma/Comic-Nico/Comic08-1.png'), require('@/assets/4WaystoManageStigma/Comic-Nico/Comic08-2.png'), require('@/assets/4WaystoManageStigma/Comic-Nico/Comic08-3.png')],
                            strategy: 4,
                            complete: false
                        },
                        {
                            id: 4,
                            name: 'Tommy',
                            image: require('@/assets/underwearPersonality/profiles/characters_Tommy.png'),
                            comics: [require('@/assets/4WaystoManageStigma/Comic-Tommy/Comic07-1.png'), require('@/assets/4WaystoManageStigma/Comic-Tommy/Comic07-2.png'), require('@/assets/4WaystoManageStigma/Comic-Tommy/Comic07-3.png')],
                            strategy: 3,
                            complete: false
                        }
                    ],
                    'id'
                );

                return peeps;
            }
        }
    };
</script>

<style scoped>
    .check {
        transform: translate(-38%, -38.5%) scale(0.18);
    }
    .completed {
        opacity: 0.5;
    }
    .incorrect-selected {
        text-decoration: line-through;
        text-decoration-color: red;
    }
</style>
